import React, { useState } from 'react';
import styles from './ContactForm.module.css'; 
import arrow from '../../assets/arrow_forward_white.png';
import { useLanguage } from '../../context/LanguageContext.js';
import globalTranslations from '../../data/translations.js';

/* const URL = 'http://localhost:8000/send_email.php' */
const URL = '/send_email.php'

function ContactForm() {
    const { language } = useLanguage();
    const translations = globalTranslations.contactPage;

    const [formData, setFormData] = useState({
        name: '',
        email: '',
        subject: '',
        message: ''
    });

    const [formStatus, setFormStatus] = useState({
        type: null,
        message: ''
    });
    
    const [isLoading, setIsLoading] = useState(false);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData({
            ...formData,
            [name]: value
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsLoading(true);
        setFormStatus({ type: null, message: '' }); 
        
        try {
            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), 10000);
            
            const response = await fetch(URL, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(formData),
                signal: controller.signal
            });
            
            clearTimeout(timeoutId);
            const contentType = response.headers.get('content-type');
            if (contentType && contentType.includes('text/html')) {
                setFormStatus({
                    type: 'error',
                    message: translations[language].serverUnavailable || 'Server temporarily unavailable. Please try again later.'
                });
                return;
            }
            let result;
            let isJsonResponse = false;
            try {
                const text = await response.text();
                result = JSON.parse(text);
                isJsonResponse = true;
            } catch (e) {
                result = await response.text();
            }
            
            if (response.ok) {
                if (isJsonResponse && result.success) {
                    setFormStatus({
                        type: 'success',
                        message: result.message || translations[language].messageSent || 'Your message has been sent. Thank you!'
                    });
                    setFormData({
                        name: '',
                        email: '',
                        subject: '',
                        message: ''
                    });
                } else if (typeof result === 'string' && result.includes('Thank you')) {
                    setFormStatus({
                        type: 'success',
                        message: result
                    });
                    setFormData({
                        name: '',
                        email: '',
                        subject: '',
                        message: ''
                    });
                } else {
                    setFormStatus({
                        type: 'success',
                        message: isJsonResponse ? result.message || JSON.stringify(result) : result
                    });
                }
            } else {
                if (isJsonResponse && result.error) {
                    setFormStatus({
                        type: 'error',
                        message: result.error
                    });
                } else {
                    setFormStatus({
                        type: 'error',
                        message: isJsonResponse ? JSON.stringify(result) : result
                    });
                }
            }
        } catch (error) {
            console.error('Error submitting the form:', error);
            
            let errorMessage = translations[language].generalError || 'There was an error submitting the form. Please try again.';
            
            if (error.name === 'AbortError') {
                errorMessage = translations[language].timeout || 'The server is taking too long to respond. Please try again later.';
            } else if (error.message && (error.message.includes('Failed to fetch') || error.message.includes('Network Error'))) {
                errorMessage = translations[language].networkError || 'Network error. Please check your connection and try again.';
            }
            
            setFormStatus({
                type: 'error',
                message: errorMessage
            });
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className={styles.form}>
            <h2 className={styles.title}>{translations[language].message}</h2>
            
            {formStatus.type === 'success' && (
                <div className={styles.successMessage}>
                    {formStatus.message}
                </div>
            )}
            {formStatus.type === 'error' && (
                <div className={styles.errorMessage}>
                    {formStatus.message}
                </div>
            )}
            
            <form onSubmit={handleSubmit} className={styles.form}>
                <input 
                    name="name" 
                    placeholder={translations[language].name} 
                    value={formData.name}
                    onChange={handleChange}
                    className={styles.input}
                    required
                />
                <input 
                    name="email" 
                    type="email"
                    placeholder={translations[language].email} 
                    value={formData.email}
                    onChange={handleChange}
                    className={styles.input}
                    required
                />
                <input 
                    name="subject" 
                    placeholder={translations[language].subject} 
                    value={formData.subject}
                    onChange={handleChange}
                    className={styles.input}
                    required
                />
                <textarea 
                    name="message" 
                    placeholder='Message' 
                    value={formData.message}
                    onChange={handleChange}
                    className={styles.input}
                    required
                />
                <button 
                    type="submit" 
                    className={styles.submit}
                    disabled={isLoading}
                >
                    {isLoading 
                        ? translations[language].sending || 'Sending...' 
                        : translations[language].sendButton
                    } 
                    {!isLoading && <img className={styles.arrow} alt="send" src={arrow} />}
                </button>
            </form>
        </div>
    );
}

export default ContactForm;
